import React from 'react';
import { Box, Text, Badge } from '@chakra-ui/react';
import { ADMIN_COLORS, ADMIN_LAYOUT } from '../../../../../generated/tokens/constants';

interface BlockErrorBoundaryProps {
  blockType: string;
  blockId: string;
  children: React.ReactNode;
}

interface BlockErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class BlockErrorBoundary extends React.Component<
  BlockErrorBoundaryProps,
  BlockErrorBoundaryState
> {
  constructor(props: BlockErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): BlockErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      `[BlockErrorBoundary] Block "${this.props.blockType}" (${this.props.blockId}) failed:`,
      error,
      errorInfo,
    );
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          gc-testing-id="blockErrorBoundary"
          p={3}
          borderRadius={ADMIN_LAYOUT.radius}
          bg={ADMIN_COLORS.card}
          border="1px solid"
          borderColor="red.800"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Badge colorScheme="red" fontSize="xs">
              Error
            </Badge>
            <Badge gc-testing-id="badgeBlockType" fontSize="xs" variant="outline">
              {this.props.blockType}
            </Badge>
            <Text fontSize="xs" color={ADMIN_COLORS.textMuted}>
              {this.props.blockId.slice(0, 8)}
            </Text>
          </Box>
          <Text fontSize="sm" color="red.300" mt={1}>
            Block failed to render
          </Text>
          {this.state.error && (
            <Text fontSize="xs" color={ADMIN_COLORS.textMuted} mt={1} noOfLines={2}>
              {this.state.error.message}
            </Text>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
